import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useStorage, useDebounceFn } from "@vueuse/core"
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { abortRequestsGetThatStartsWith, isBackendRequestPendingThatStartWith } from "@/utils/backend.js"


/**
 * Storage Transactions Store
 *
 * Manages warehouse transactions (supply type=2, usage type=1) including:
 * - Fetching transactions from GET /storeTransactions/index (with BE pagination)
 * - Fetching filters from GET /storeTransactions/filters?type=X
 * - Server-side filtering (suppliers, contacts)
 * - Server-side sorting
 * - Server-side search
 */
export const useStorageTransactionsStore = defineStore('storageTransactionsStore', () => {

  const { t } = useI18n()
  const DEFAULT_UNIT = "szt"

  // ========================================
  // State
  // ========================================

  /** Global loading state for transactions operations */
  const isLoading = ref(true)

  /** Transactions for the current page from API */
  const allTransactions = ref([])

  /** Whether data has been fetched */
  const isLoaded = ref(false)

  /** Transaction type (1=usage, 2=supply) */
  const transactionType = ref(null)

  const route = useRoute()
  const router = useRouter()

  /**
   * Pagination metadata from API
   * @property {number} total - Total transactions matching current filters
   * @property {number} perPage - Items per page
   * @property {number} page - Current page
   * @property {number} totalAll - Total transactions (regardless of filters)
   */
  const meta = ref({
    total: 0,
    perPage: 10,
    page: 1,
    totalAll: 0,
  })

  /**
   * Available filters from API
   * @property {Array<{id: string, name: string}>} suppliers
   * @property {Array<{id: string, name: string}>} contacts
   */
  const availableFilters = ref({
    suppliers: [],
    contacts: [],
    statuses: [],
  })

  /** Loading state for filters */
  const isLoadingFilters = ref(false)

  /** Current transaction being viewed/edited */
  const currentTransaction = ref({})

  /** Products of the current transaction */
  const currentTransactionProducts = ref([])

  /** Parent document of the current transaction (if it is a correction) */
  const currentTransactionParent = ref(null)

  /** Corrections related to the current transaction */
  const currentTransactionCorrections = ref([])

  /** Inventory that generated the current transaction (null when unrelated to a stocktaking) */
  const currentTransactionInventory = ref(null)

  /** Loading state for transaction detail */
  const isLoadingTransaction = ref(false)

  /** Product search results for autocomplete */
  const productSearchResults = ref([])

  /** Loading state for product search */
  const isSearchingProducts = ref(false)

  /** Loading state for adding a product to the transaction */
  const isAddingProduct = ref(false)

  /** Loading state for updating transaction form */
  const isUpdating = ref(false)

  /** Default filter values for each transaction type */
  const defaultFilters = { search: '', supplier: null, contact: null, status: null, documentDateFrom: null, documentDateTo: null, supplyDateFrom: null, supplyDateTo: null, sorting: { column: 'id', direction: 'desc' } }

  /** Filters stored per transaction type (1=usage, 2=supply) */
  const filtersByType = useStorage('storage-transactions-filters', {
    1: { ...defaultFilters, sorting: { ...defaultFilters.sorting } },
    2: { ...defaultFilters, sorting: { ...defaultFilters.sorting } },
  }, localStorage, {
    listenToStorageChanges: false,
    mergeDefaults: true,
  })

  /** Resolve current type, fallback to 2 (supply) */
  const _currentType = () => transactionType.value || 2

  /** Search query for filtering transactions */
  const searchQuery = computed({
    get: () => filtersByType.value[_currentType()]?.search ?? '',
    set: (v) => { filtersByType.value[_currentType()].search = v },
  })

  /** Selected supplier filter */
  const selectedSupplier = computed({
    get: () => filtersByType.value[_currentType()]?.supplier ?? null,
    set: (v) => { filtersByType.value[_currentType()].supplier = v },
  })

  /** Selected contact filter */
  const selectedContact = computed({
    get: () => filtersByType.value[_currentType()]?.contact ?? null,
    set: (v) => { filtersByType.value[_currentType()].contact = v },
  })

  /** Selected status filter */
  const selectedStatus = computed({
    get: () => filtersByType.value[_currentType()]?.status ?? null,
    set: (v) => { filtersByType.value[_currentType()].status = v },
  })

  /** Document date from filter */
  const documentDateFrom = computed({
    get: () => filtersByType.value[_currentType()]?.documentDateFrom ?? null,
    set: (v) => { filtersByType.value[_currentType()].documentDateFrom = v },
  })

  /** Document date to filter */
  const documentDateTo = computed({
    get: () => filtersByType.value[_currentType()]?.documentDateTo ?? null,
    set: (v) => { filtersByType.value[_currentType()].documentDateTo = v },
  })

  /** Supply date from filter */
  const supplyDateFrom = computed({
    get: () => filtersByType.value[_currentType()]?.supplyDateFrom ?? null,
    set: (v) => { filtersByType.value[_currentType()].supplyDateFrom = v },
  })

  /** Supply date to filter */
  const supplyDateTo = computed({
    get: () => filtersByType.value[_currentType()]?.supplyDateTo ?? null,
    set: (v) => { filtersByType.value[_currentType()].supplyDateTo = v },
  })

  /**
   * Sorting configuration
   * @property {string|null} column - Column to sort by (id, invoice, supplier, contact, documentDate)
   * @property {string} direction - Sort direction (asc, desc)
   */
  const sorting = computed({
    get: () => filtersByType.value[_currentType()]?.sorting ?? { column: 'id', direction: 'desc' },
    set: (v) => { filtersByType.value[_currentType()].sorting = v },
  })

  // ========================================
  // Getters
  // ========================================

  /**
   * Total count of transactions (all, regardless of filters)
   * @returns {number}
   */
  const totalTransactionsCount = computed(() => meta.value.totalAll)

  /**
   * Filtered transactions count (matching current filters)
   * @returns {number}
   */
  const filteredTransactionsCount = computed(() => meta.value.total)

  /**
   * Total pages based on pagination metadata
   * @returns {number}
   */
  const totalPages = computed(() => {
    if (!meta.value.total || !meta.value.perPage) return 1

    return Math.ceil(meta.value.total / meta.value.perPage)
  })

  /**
   * Current page from pagination metadata
   * @returns {number}
   */
  const currentPage = computed(() => meta.value.page)

  /**
   * Count of active filters
   * @returns {number}
   */
  const filtersCount = computed(() => {
    let count = 0
    if (selectedSupplier.value) count++
    if (selectedContact.value) count++
    if (selectedStatus.value) count++
    if (documentDateFrom.value) count++
    if (documentDateTo.value) count++
    if (supplyDateFrom.value) count++
    if (supplyDateTo.value) count++

    return count
  })

  // ========================================
  // Actions
  // ========================================

  /**
   * Format a Date value to YYYY-MM-DD string for API params
   * @param {Date|string} date
   * @returns {string}
   */
  const formatDateParam = (date) => {
    if (!date) return ''
    const d = new Date(date)

    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }

  /**
   * Clear all filters
   */
  const clearFilters = async () => {
    console.warn('clearFilters')
    selectedSupplier.value = null
    selectedContact.value = null
    selectedStatus.value = null
    documentDateFrom.value = null
    documentDateTo.value = null
    supplyDateFrom.value = null
    supplyDateTo.value = null
    searchQuery.value = ''

    await router.replace({
      path: route.path,
      query: { type: transactionType.value },
    })

    await fetchTransactions({
      page: 1,
      perPage: meta.value.perPage,
      force: true,
    })
  }

  /**
   * Fetch available filters from the backend
   *
   * @returns {Promise<boolean>} True if successful, false otherwise
   */
  const fetchFilters = async () => {
    isLoadingFilters.value = true

    try {
      const response = await $backend(`/storeTransactions/filters?type=${transactionType.value}`, {
        method: 'GET',
      })

      if (response.success) {
        availableFilters.value = {
          suppliers: response.data.filters.salonerSuppliers || [],
          contacts: response.data.filters.contacts || [],
          statuses: response.data.filters.status || [],
        }

        return true
      }
    } catch (error) {
      console.error('Error fetching transaction filters:', error)

      return false
    } finally {
      isLoadingFilters.value = false
    }

    return false
  }

  /**
   * Fetch transactions from the backend (with pagination and server-side filters)
   *
   * @param {Object} options - Fetch options
   * @param {number} options.page - Page number
   * @param {number} options.perPage - Items per page
   * @param {boolean} options.force - Force refetch even if loaded
   * @returns {Promise<boolean>} True if successful, false otherwise
   */
  const fetchTransactions = async ({ page = 1, perPage = 10, force = false } = {}) => {
    // Skip fetching if data is already loaded for the same page
    if (!force && isLoaded.value && meta.value.page === page) {
      return true
    }
    isLoading.value = true

    try {
      // Build query parameters
      const params = new URLSearchParams({
        type: transactionType.value,
        page: page.toString(),
        perPage: perPage.toString(),
      })

      // Add sorting if specified
      if (sorting.value.column) {
        params.append('orders[0][column]', sorting.value.column)
        params.append('orders[0][direction]', sorting.value.direction)
      }

      // Add filters
      if (searchQuery.value.trim()) {
        params.append('filters[search]', searchQuery.value.trim())
      }
      if (selectedSupplier.value) {
        params.append('filters[salonerSupplierId]', selectedSupplier.value)
      }
      if (selectedContact.value) {
        params.append('filters[contact]', selectedContact.value)
      }
      if (selectedStatus.value) {
        params.append('filters[status]', selectedStatus.value)
      }
      if (documentDateFrom.value) {
        params.append('filters[documentDateFrom]', formatDateParam(documentDateFrom.value))
      }
      if (documentDateTo.value) {
        params.append('filters[documentDateTo]', formatDateParam(documentDateTo.value))
      }
      if (supplyDateFrom.value) {
        params.append('filters[supplyDateFrom]', formatDateParam(supplyDateFrom.value))
      }
      if (supplyDateTo.value) {
        params.append('filters[supplyDateTo]', formatDateParam(supplyDateTo.value))
      }

      const response = await $backend(`/storeTransactions/index?${params.toString()}`, {
        method: 'GET',
      })
      if (response.success) {
        // If we are on far away page and there is no results for this page we go to last page instead
        if (response.data.transactions.length === 0 && page !== 1 && response.meta?.total > 0) {
          isLoaded.value = false
          const lastPage = Math.ceil(response.meta.total / (response.meta?.perPage ?? perPage))
          await router.push({ path: '/storage/transactions', query: { type: transactionType.value, page: lastPage } })

          return true
        }

        allTransactions.value = response.data.transactions || []
        meta.value = {
          total: response.meta?.total ?? 0,
          perPage: response.meta?.perPage ?? perPage,
          page: response.meta?.page ?? page,
          totalAll: response.meta?.totalAll ?? 0,
        }

        isLoaded.value = true
        isLoading.value = false
        
        return true
      }

      isLoading.value = false
    } catch (error) {
      console.log('errr', error)
      isLoading.value = false

      return false
    }

    isLoading.value = false

    return false
  }

  /**
   * Change page and refetch transactions
   *
   * @param {number} page - Page number
   * @returns {Promise<boolean>}
   */
  const changePage = async (page) => {
    return await fetchTransactions({
      page,
      perPage: meta.value.perPage,
      force: true,
    })
  }

  /**
   * Change items per page and refetch transactions (reset to page 1)
   *
   * @param {number} perPage - Items per page
   * @returns {Promise<boolean>}
   */
  const changePerPage = async (perPage) => {
    return await fetchTransactions({
      page: 1,
      perPage,
      force: true,
    })
  }

  /**
   * Debounced action: updates search query and refetches transactions after 300 ms of inactivity
   *
   * @param {string} query - Search query
   * @returns {Promise<boolean>}
   */
  const debouncedSetSearchQuery = useDebounceFn(async (query) => {
    console.log('debouncedSetSearchQuery', query)
    searchQuery.value = query

    return await fetchTransactions({
      page: 1,
      perPage: meta.value.perPage,
      force: true,
    })
  }, 300)

  /**
   * Set supplier filter and refetch transactions (reset to page 1)
   *
   * @param {string|null} id - Supplier ID or null to clear
   * @returns {Promise<boolean>}
   */
  const setSupplierFilter = async (id) => {
    selectedSupplier.value = id

    return await fetchTransactions({
      page: 1,
      perPage: meta.value.perPage,
      force: true,
    })
  }

  /**
   * Set contact filter and refetch transactions (reset to page 1)
   *
   * @param {string|null} id - Contact ID or null to clear
   * @returns {Promise<boolean>}
   */
  const setContactFilter = async (id) => {
    selectedContact.value = id

    return await fetchTransactions({
      page: 1,
      perPage: meta.value.perPage,
      force: true,
    })
  }

  /**
   * Set status filter and refetch transactions (reset to page 1)
   *
   * @param {string|null} id - Status ID or null to clear
   * @returns {Promise<boolean>}
   */
  const setStatusFilter = async (id) => {
    selectedStatus.value = id

    return await fetchTransactions({
      page: 1,
      perPage: meta.value.perPage,
      force: true,
    })
  }

  /**
   * Set sorting and refetch transactions (reset to page 1)
   *
   * @param {string|null} column - Column to sort by
   * @param {string} direction - Sort direction (asc/desc)
   * @returns {Promise<boolean>}
   */
  const setSorting = async (column, direction = 'desc') => {
    sorting.value = { column, direction }

    return await fetchTransactions({
      page: 1,
      perPage: meta.value.perPage,
      force: true,
    })
  }

  /**
   * Clear all filters and refetch transactions
   *
   * @returns {Promise<boolean>}
   */
  const clearAllFilters = async () => {
    console.log('clearAllFilters')
    searchQuery.value = ''
    selectedSupplier.value = null
    selectedContact.value = null
    selectedStatus.value = null
    documentDateFrom.value = null
    documentDateTo.value = null
    supplyDateFrom.value = null
    supplyDateTo.value = null
    sorting.value = { column: 'id', direction: 'desc' }

    return await fetchTransactions({
      page: 1,
      perPage: meta.value.perPage,
      force: true,
    })
  }

  /**
   * Fetch a single transaction with its products
   *
   * @param {number} id - Transaction ID
   * @returns {Promise<boolean>} True if successful
   */
  const fetchTransaction = async (id) => {
    isLoadingTransaction.value = true

    try {
      const response = await $backend(`/storeTransactions/show?id=${id}`, {
        method: 'GET',
      })

      if (response.success) {
        currentTransaction.value = response.data.transaction
        currentTransactionProducts.value = response.data.products || []
        currentTransactionParent.value = response.data.root || null

        // "connected" holds the whole chain — skip only the root (shown as its own alert)
        currentTransactionCorrections.value = (response.data.connected || []).filter(
          t => t.id !== response.data.root?.id,
        )
        currentTransactionInventory.value = response.data.inventory || null

        return true
      }
    } catch (error) {
      console.error('Error fetching transaction:', error)

      return false
    } finally {
      isLoadingTransaction.value = false
    }

    return false
  }

  /**
   * Search products for autocomplete
   *
   * @param {string} query - Search query
   * @returns {Promise<boolean>} True if successful
   */
  const searchProducts = async (query) => {
    if (!query || query.trim().length < 1) {
      productSearchResults.value = []

      return true
    }

    abortRequestsGetThatStartsWith('/store/index')

    isSearchingProducts.value = true

    try {
      const params = new URLSearchParams({
        'filters[search]': query.trim(),
      })

      const response = await $backend(`/store/index?${params.toString()}`, {
        method: 'GET',
      })

      if (response.success) {
        const products = response.data.products || []

        if (products.length === 0) {
          productSearchResults.value = [{
            id: '__add_new__',
            name: t('storage.transactions.detail_product_not_found_add_new'),
          }]
        } else {
          productSearchResults.value = products
        }

        return true
      }
    } catch (error) {
      console.error('Error searching products:', error)

      return false
    } finally {
      if(!isBackendRequestPendingThatStartWith('/store/index')) isSearchingProducts.value = false
    }

    return false
  }

  /**
   * Add a product to a transaction via backend
   *
   * @param {Object} body - Request payload
   * @returns {Promise<Object>} Backend response
   */
  const addProductToTransaction = async (body) => {
    isAddingProduct.value = true

    try {
      return await $backend('/storeTransactions/addProduct', {
        method: 'POST',
        body,
      })
    } catch (error) {
      //console.error('Error adding product to transaction:', error)

      return { success: false }
    } finally {
      isAddingProduct.value = false
    }
  }

  /**
   * Update transaction form+products via backend
   *
   * @param {Object} body - Request payload
   * @returns {Promise<Object>} Backend response
   */
  const updateTransaction = async (body) => {
    isUpdating.value = true

    // Łukasz does not want "store" in "products" anymore ;(
    const sanitizedBody = {
      ...body,
      products: Array.isArray(body?.products)
        ? body.products.map(({ store, ...product }) => product)
        : body?.products,
    }

    try {
      return await $backend('/storeTransactions/save', {
        method: 'POST',
        body: sanitizedBody,
      })
    } catch (error) {
      //console.error('Error adding product to transaction:', error)

      return { success: false }
    } finally {
      isUpdating.value = false
    }
  }

  /**
   * Delete a transaction
   *
   * @param {number} id - Transaction ID
   * @returns {Promise<Object>} Backend response
   */
  const deleteTransaction = async (id) => {
    try {
      return await $backend('/storeTransactions/delete', {
        method: 'POST',
        body: { id },
      })
    } catch (error) {
      return { success: false }
    }
  }

  /**
   * Close a transaction
   *
   * @param {number} id - Transaction ID
   * @returns {Promise<Object>} Backend response
   */
  const closeTransaction = async (id) => {
    try {
      return await $backend('/storeTransactions/close', {
        method: 'POST',
        body: { id },
      })
    } catch (error) {
      return { success: false }
    }
  }

  /**
   * Create a correction document for a transaction
   *
   * @param {number} id - Transaction ID
   * @returns {Promise<Object>} Backend response
   */
  const correctTransaction = async (id) => {
    try {
      return await $backend('/storeTransactions/correct', {
        method: 'POST',
        body: { id },
      })
    } catch (error) {
      return { success: false }
    }
  }

  /**
   * Cancel a transaction
   *
   * @param {number} id - Transaction ID
   * @returns {Promise<Object>} Backend response
   */
  const cancelTransaction = async (id) => {
    try {
      return await $backend('/storeTransactions/cancel', {
        method: 'POST',
        body: { id },
      })
    } catch (error) {
      return { success: false }
    }
  }

  /**
   * Reset transaction detail state
   */
  const resetTransactionDetail = () => {
    currentTransaction.value = null
    currentTransactionProducts.value = []
    currentTransactionParent.value = null
    currentTransactionCorrections.value = []
    currentTransactionInventory.value = null
    isLoadingTransaction.value = false
    productSearchResults.value = []
    isSearchingProducts.value = false
  }

  /**
   * Reset store to initial state
   */
  const resetStore = () => {
    console.log('resetStore')

    isLoading.value = false
    isLoadingFilters.value = false
    allTransactions.value = []
    isLoaded.value = false
    transactionType.value = null
    meta.value = { total: 0, perPage: 10, page: 1, totalAll: 0 }
    availableFilters.value = {
      suppliers: [],
      contacts: [],
      statuses: [],
    }
    //searchQuery.value = ''
    //selectedSupplier.value = null
    //selectedContact.value = null
    //selectedStatus.value = null
    //sorting.value = { column: 'id', direction: 'desc' }
  }

  // ========================================
  // Exports
  // ========================================

  return {
    // CONST
    DEFAULT_UNIT,

    // State
    isLoading,
    isLoadingFilters,
    isLoadingTransaction,
    isUpdating,
    allTransactions,
    currentTransaction,
    currentTransactionProducts,
    currentTransactionParent,
    currentTransactionCorrections,
    currentTransactionInventory,
    productSearchResults,
    isSearchingProducts,
    isAddingProduct,
    isLoaded,
    transactionType,
    meta,
    availableFilters,
    searchQuery,
    selectedSupplier,
    selectedContact,
    selectedStatus,
    documentDateFrom,
    documentDateTo,
    supplyDateFrom,
    supplyDateTo,
    sorting,

    // Getters
    totalPages,
    currentPage,
    filtersCount,
    totalTransactionsCount,
    filteredTransactionsCount,

    // Actions
    fetchFilters,
    fetchTransactions,
    changePage,
    changePerPage,
    debouncedSetSearchQuery,
    setSupplierFilter,
    setContactFilter,
    setStatusFilter,
    setSorting,
    clearAllFilters,
    resetStore,
    clearFilters,
    fetchTransaction,
    resetTransactionDetail,
    searchProducts,
    addProductToTransaction,
    updateTransaction,
    deleteTransaction,
    closeTransaction,
    correctTransaction,
    cancelTransaction,
  }
})
